import React, { useState, useEffect, useCallback } from 'react';
import { styled, css, Button, Flex, Modal, Input } from '@goodrx/matisse';
import { useSynapse } from 'app/lib/synaptik';
import { MessageProps } from 'app/components/widgets/chat/message-types';

const EMOJIS = [
  { score: 1, emoji: '😤', ariaLabel: 'face with steam from nose' },
  { score: 2, emoji: '🙁', ariaLabel: 'slightly frowning face' },
  { score: 3, emoji: '😐', ariaLabel: 'neutral face' },
  { score: 4, emoji: '🙂', ariaLabel: 'slightly smiling face' },
  { score: 5, emoji: '😍', ariaLabel: 'smiling face with heart-eyes' },
];

const NpsContainer = styled.div`
  ${(p) => css`
    border-radius: ${p.theme.radius}px;
    background: ${p.theme.colors.greyLightest};
    padding: ${p.theme.space[4]}px;
    margin: 10px 0;
    text-align: center;
    color: ${p.theme.colors.greyDarkest};
    max-width: 75%;
  `}
`;

const EmojiContainer = styled.button<any>`
  cursor: pointer;
  outline: none;
  border: none;
  background: transparent;
  opacity: 1;
  ${(p) => css`
    opacity: ${p.selectedEmoji && !p.isSelected && 0.4};
    padding-left: ${p.theme.space[2]}px;
    padding-right: ${p.theme.space[2]}px;
  `}
`;

const EmojiIcon = styled.span`
  font-size: 30px;
`;

const FeedBackInput = styled(Input)`
  resize: none;
  margin-bottom: ${(p) => p.theme.space[1]}px;
`;

const ThankYouText = styled.p`
  margin: 0;
  padding: 0;
  font-weight: 700;
`;

const ModalHeaderText = styled.span`
  font-size: 14px;
`;

const CharacterCount = styled.small`
  display: block;
  ${(p) => css`
    margin-bottom: ${p.theme.space[1]}px;
    color: ${p.theme.colors.greyDarker};
  `}
`;

const FIVE_SECONDS = 5000;
const MAX_RESPONSE_CHARACTERS = 1000;

export const NpsMessage: React.FC<MessageProps> = ({ message }) => {
  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false);
  const [selectedEmoji, setSelectedEmoji] = useState<string>();
  const [npsRecordId, setNpsRecordId] = useState<number>();
  const [feedback, setFeedback] = useState('');
  const [wasFeedbackModalOpened, setWasFeedbackModalOpened] = useState(false);

  const { submitScore, submitFeedback, chat: chatStore, messages } = useSynapse(({ nps, chat }) => ({
    chat,
    messages: chat.state.messages,
    submitScore: nps.submitScore,
    submitFeedback: nps.submitFeedback,
  }));

  const clearNpsMessageFromChat = useCallback((messageId) => {
    if (wasFeedbackModalOpened && !isFeedbackFormOpen) {
      setTimeout(() => {
        chatStore.setState({
          messages: messages.filter((m) => m.id !== messageId),
        });
      }, FIVE_SECONDS);
    }
  }, []);

  useEffect(() => {
    clearNpsMessageFromChat(message.id);
  }, [wasFeedbackModalOpened, isFeedbackFormOpen, messages.length, message.id]);

  const toggleFeedbackForm = () => setIsFeedbackFormOpen((isOpen) => !isOpen);

  const handleEmojiClick = async (emoji: string, score: number) => {
    if (wasFeedbackModalOpened) return;

    setSelectedEmoji(emoji);
    toggleFeedbackForm();
    setWasFeedbackModalOpened(true);

    const { id } = await submitScore(score, message.id);
    setNpsRecordId(id);
  };

  const handleFeedbackSubmission = async () => {
    if (npsRecordId) {
      await submitFeedback(feedback.trim(), npsRecordId);
    }

    toggleFeedbackForm();
  };

  const showThankYouText = wasFeedbackModalOpened && !isFeedbackFormOpen;

  return (
    <NpsContainer data-qa="nps_message">
      {showThankYouText ? <ThankYouText>Thank you for your feedback!</ThankYouText> : message.body}

      {!showThankYouText && (
        <Flex justifyContent="space-evenly" mb={-16}>
          {EMOJIS.map(({ emoji, score, ariaLabel }) => (
            <EmojiContainer
              data-qa={`emoji_btn_${score}`}
              key={score}
              isSelected={emoji === selectedEmoji}
              selectedEmoji={selectedEmoji}
              onClick={() => handleEmojiClick(emoji, score)}>
              <EmojiIcon role="img" aria-label={ariaLabel}>
                {emoji}
              </EmojiIcon>
            </EmojiContainer>
          ))}
        </Flex>
      )}

      <Modal data-qa="nps_modal" minWidth={300} open={isFeedbackFormOpen} onClose={toggleFeedbackForm}>
        <Modal.Header>
          <ModalHeaderText data-qa="nps_modal_header">You chose {selectedEmoji}</ModalHeaderText>
        </Modal.Header>

        <Modal.Body>
          <FeedBackInput
            data-qa="nps_modal_input"
            maxLength={MAX_RESPONSE_CHARACTERS}
            value={feedback}
            onChange={(_: any, val: any) => setFeedback(val)}
            placeholder="Tell us a bit more about why here..."
            rows={8}
            multiline
          />

          <CharacterCount data-qa="nps_modal_character_count">
            {MAX_RESPONSE_CHARACTERS - feedback.length} characters left
          </CharacterCount>

          <Button data-qa="nps_modal_submit_btn" variant="primary" block onClick={handleFeedbackSubmission}>
            Submit
          </Button>
        </Modal.Body>
      </Modal>
    </NpsContainer>
  );
};
